import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Spinner, Button, Modal } from "react-bootstrap";
import { 
  FormControl as MUIFormControl, 
  InputLabel, 
  Select as MUISelect, 
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TextField
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import SlotTimesModal from "./SlotTimesModal";
import ProjectDataModal from "./ProjectDataModal";
import authService from "../../../utils/authService";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const MONTH_NAMES_TO_NUMBER: { [key: string]: number } = {
  "Jan": 0, "Feb": 1, "Mar": 2, "Apr": 3, "May": 4, "Jun": 5,
  "Jul": 6, "Aug": 7, "Sep": 8, "Oct": 9, "Nov": 10, "Dec": 11
};

interface Course {
  course_id: string;
  course_name: string;
  batches_count?: number;
}

interface BatchProject {
  batch_id: string;
  batch_name: string;
  slots: number;
  saturday_holiday: boolean;
  sunday_holiday: boolean;
  no_of_hours: string;
  start_date: string;
  end_date: string;
  slot_start_time: string;
  slot_end_time: string;
  project_data: any;
  project_changed_since_last_tag: boolean;
}

interface BatchProjectResponse {
  batch_project: BatchProject[];
  project_data: any;
  project_last_updatable_tag?: string;
}

interface Rule {
  id: string;
  level: string;
  score: string;
  time: string;
}

interface RulesResponse {
  mcq: Rule[];
  coding: Rule[];
  project_coding?: Rule[];
}

interface ProjectConfigIssue {
  phase: string;
  part?: string;
  task?: string;
  subtask?: string;
  detail: string;
}

interface ProjectData {
  content: Array<{
    phase_name: string;
    parts: Array<{
      part_name: string;
      tasks: Array<{
        task_name: string;
        data: Array<{
          id?: string;
          time?: string;
          type: string;
          topic_id: string;
          subject_id: string;
          is_mandatory: boolean;
          sub_topic_id: string;
          count?: {
            level1: string;
            level2: string;
            level3: string;
          };
          selected?: {
            level1: string;
            level2: string;
            level3: string;
          };
        }>;
      }>;
    }>;
  }>;
}

const ProjectBatchMap: React.FC = () => {
  const location = useLocation();
  
  // Get project_id from URL query parameters
  const urlParams = new URLSearchParams(location.search);
  const projectId = urlParams.get('id') || '';

  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string>("");
  const [batchProjects, setBatchProjects] = useState<BatchProject[]>([]);
  const [apiResponse, setApiResponse] = useState<BatchProjectResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingBatches, setLoadingBatches] = useState<boolean>(false);
  const [selectedBatches, setSelectedBatches] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [projectLastUpdatableTag, setProjectLastUpdatableTag] = useState<string>("");
  const [localBatchData, setLocalBatchData] = useState<Record<string, { 
    slots: number; 
    saturday_holiday: boolean; 
    sunday_holiday: boolean;
    start_date: string;
    no_of_hours: string;
  }>>({});
  const [showSlotTimesModal, setShowSlotTimesModal] = useState<boolean>(false);
  const [showMapModal, setShowMapModal] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showConfigModal, setShowConfigModal] = useState<boolean>(false);
  const [configModalIssues, setConfigModalIssues] = useState<ProjectConfigIssue[]>([]);
  const [rules, setRules] = useState<RulesResponse | null>(null);
  const [expandedPhases, setExpandedPhases] = useState<Set<number>>(new Set([0])); // First phase open by default
  const [expandedParts, setExpandedParts] = useState<Record<string, Set<number>>>({ "0": new Set([0]) }); // First part of first phase open by default
  const [taskDays, setTaskDays] = useState<Record<string, number>>({}); // Store custom days for each task
  const [slotStartTime, setSlotStartTime] = useState<string>("");
  const [slotEndTime, setSlotEndTime] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);

  /**
   * Effect: Initialize component data on mount and when projectId changes
   * Dependencies:
   * - projectId: Project ID from URL - triggers refetch when changed
   */
  useEffect(() => {
    fetchRules();
    const loadCourses = async () => {
      await fetchCourses();
      if (projectId) {
        await fetchCoursesWithChangedProjects();
      }
    };
    loadCourses();
  }, [projectId]);

  /**
   * Effect: Fetch batch projects when course or project changes
   * Dependencies:
   * - selectedCourse: Selected course ID - triggers fetch when changed
   * - projectId: Project ID from URL - triggers fetch when changed
   */
  useEffect(() => {
    if (selectedCourse && projectId) {
      fetchBatchProjects(selectedCourse, projectId);
    } else {
      setBatchProjects([]);
      setLocalBatchData({});
      setSelectedBatches(new Set());
      setSelectAll(false);
      setApiResponse(null);
      setTaskDays({});
    }
  }, [selectedCourse, projectId]);

  /**
   * Compares two project_data objects to check if they are the same
   * Uses JSON stringify for deep comparison
   */
  const areProjectDataEqual = (data1: any, data2: any): boolean => {
    if (data1 === data2) return true;
    if (!data1 || !data2) return false;
    try {
      return JSON.stringify(data1) === JSON.stringify(data2);
    } catch (error) {
      return false;
    }
  };

  /**
   * Gets the common project_data for selected batches
   * Returns the project_data if all selected batches with data have the same project_data
   * Returns null if batches have different project_data or no batches have data
   */
  const getCommonProjectData = (): any => {
    // Step 1: Check if any batches are selected
    if (selectedBatches.size === 0) return null;
    
    // Step 2: Map selected batch IDs to actual batch objects
    const selectedBatchesList = Array.from(selectedBatches)
      .map(id => batchProjects.find(b => b.batch_id === id))
      .filter(b => b !== undefined) as BatchProject[];
    
    // Step 3: Filter to only batches that have project_data
    const batchesWithData = selectedBatchesList.filter(
      b => b.project_data !== null && b.project_data !== undefined
    );
    
    // Step 4: If no batches have data, return null
    if (batchesWithData.length === 0) return null;
    
    // Step 5: If only one batch has data, return its project_data
    if (batchesWithData.length === 1) return batchesWithData[0].project_data;
    
    // Step 6: Check if all batches with data have the same project_data
    const firstData = batchesWithData[0].project_data;
    const allSame = batchesWithData.every(b => areProjectDataEqual(b.project_data, firstData));
    
    // Step 7: Return the common project_data if all same, null otherwise
    return allSame ? firstData : null;
  };

  // Step 1: Convert Set to sorted array for stable dependency comparison
  // This ensures useMemo dependencies work correctly (Sets are compared by reference)
  const selectedBatchesArray = useMemo(() => Array.from(selectedBatches).sort(), [selectedBatches]);

  // Step 2: Memoize common project_data to avoid recalculating expensive deep comparisons
  // Dependencies: selectedBatchesArray (stable array), batchProjects (batch data)
  const commonProjectData = useMemo(() => getCommonProjectData(), [selectedBatchesArray, batchProjects]);

  // Helper function to extract taskDays from project data structure
  // No need for useCallback since it's only used in useEffect and has no dependencies
  const extractTaskDaysFromData = (data: any): Record<string, number> => {
    // Step 1: Initialize empty taskDays object
    const initialTaskDays: Record<string, number> = {};
    
    // Step 2: Check if data structure is valid
    if (!data || !data.content) return initialTaskDays;
    
    // Step 3-4: Iterate through phases, parts, and tasks to extract no_of_days
    data.content.forEach((phase: any, phaseIndex: number) => {
      if (phase.parts) {
        phase.parts.forEach((part: any, partIndex: number) => {
          if (part.tasks) {
            part.tasks.forEach((task: any, taskIndex: number) => {
              if (task.no_of_days !== undefined && task.no_of_days !== null) {
                // Step 4: Store with key format "phaseIndex-partIndex-taskIndex"
                const taskKey = `${phaseIndex}-${partIndex}-${taskIndex}`;
                initialTaskDays[taskKey] = task.no_of_days;
              }
            });
          }
        });
      }
    });
    
    // Step 5: Return the populated taskDays object
    return initialTaskDays;
  };

  /**
   * Parses the project_last_updatable_tag to extract phase, part, task, and subtask numbers
   * Format: "phase1#part1#task1#subtask5"
   */
  const parseUpdatableTag = (tag: string): { phase: number; part: number; task: number; subtask: number } | null => {
    if (!tag || tag.trim() === "") return null;
    
    const parts = tag.split('#');
    if (parts.length !== 4) return null;
    
    try {
      const phaseNum = parseInt(parts[0].replace('phase', ''), 10);
      const partNum = parseInt(parts[1].replace('part', ''), 10);
      const taskNum = parseInt(parts[2].replace('task', ''), 10);
      const subtaskNum = parseInt(parts[3].replace('subtask', ''), 10);
      
      if (isNaN(phaseNum) || isNaN(partNum) || isNaN(taskNum) || isNaN(subtaskNum)) {
        return null;
      }
      
      return { phase: phaseNum, part: partNum, task: taskNum, subtask: subtaskNum };
    } catch (error) {
      return null;
    }
  };

  /**
   * Extracts number from ID string (e.g., "phase1" -> 1)
   */
  const extractNumberFromId = (id: string, prefix: string): number => {
    const num = parseInt(id.replace(prefix, ''), 10);
    return isNaN(num) ? 0 : num;
  };

  /**
   * Checks if a subtask is restricted based on project_last_updatable_tag
   * The tag marks the LAST updatable item (inclusive), so items at or before the tag are restricted
   */
  const isSubtaskRestricted = (phaseId: string, partId: string, taskId: string, subtaskId: string): boolean => {
    if (!projectLastUpdatableTag) return false;
    const tagData = parseUpdatableTag(projectLastUpdatableTag);
    if (!tagData) return false;
    
    const phaseNum = extractNumberFromId(phaseId, 'phase');
    const partNum = extractNumberFromId(partId, 'part');
    const taskNum = extractNumberFromId(taskId, 'task');
    const subtaskNum = extractNumberFromId(subtaskId, 'subtask');
    
    // Restrict if phase is before the tag phase
    if (phaseNum < tagData.phase) return true;
    // Restrict if same phase and part is before the tag part
    if (phaseNum === tagData.phase && partNum < tagData.part) return true;
    // Restrict if same phase/part and task is before the tag task
    if (phaseNum === tagData.phase && partNum === tagData.part && taskNum < tagData.task) return true;
    // Restrict if same phase/part/task and subtask is at or before the tag subtask
    if (phaseNum === tagData.phase && partNum === tagData.part && taskNum === tagData.task && subtaskNum <= tagData.subtask) return true;
    return false;
  };

  /**
   * Helper function to merge arrays based on restriction logic
   * Generic recursive function that handles merging at any hierarchy level
   * Step-by-step:
   * 1. Initialize empty merged array
   * 2. Process batch items:
   *    - If before restriction: use batch data entirely
   *    - If after restriction: use main data entirely
   *    - If at restriction: merge using mergeFn if provided
   * 3. Add remaining main items after restriction level (not already added)
   * 4. Return merged array
   */
  const mergeItems = (
    batchItems: any[],
    mainItems: any[],
    tagData: { phase: number; part: number; task: number; subtask: number },
    level: 'phase' | 'part' | 'task' | 'subtask',
    levelNum: number,
    getId: (item: any, index: number) => string,
    getIndex: (id: string, prefix: string) => number,
    mergeFn?: (batchItem: any, mainItem: any) => any,
    useInclusiveComparison: boolean = false
  ): any[] => {
    const merged: any[] = [];
    const prefix = level === 'phase' ? 'phase' : level === 'part' ? 'part' : level === 'task' ? 'task' : 'subtask';
    
    // Process batch items
    batchItems.forEach((batchItem: any, index: number) => {
      const itemId = getId(batchItem, index);
      const itemNum = getIndex(itemId, prefix);
      
      if (itemNum < levelNum) {
        // Before restricted - use batch data entirely
        merged.push({ ...batchItem });
      } else if (itemNum > levelNum) {
        // After restricted - use main data entirely
        const mainItem = mainItems.find((m: any) => getId(m, 0) === itemId);
        if (mainItem) {
          merged.push({ ...mainItem });
        }
      } else {
        // Same level - merge if mergeFn provided, otherwise use batch
        if (mergeFn) {
          const mainItem = mainItems.find((m: any) => getId(m, 0) === itemId);
          if (mainItem) {
            merged.push(mergeFn(batchItem, mainItem));
          } else {
            merged.push({ ...batchItem });
          }
        } else {
          // For subtask level with inclusive comparison, check if <= levelNum
          if (useInclusiveComparison && itemNum <= levelNum) {
            merged.push({ ...batchItem });
          } else if (!useInclusiveComparison) {
            merged.push({ ...batchItem });
          }
        }
      }
    });
    
    // Add remaining main items after restricted level
    mainItems.forEach((mainItem: any, index: number) => {
      const itemId = getId(mainItem, index);
      const itemNum = getIndex(itemId, prefix);
      
      // Only add if after restricted level and not already added
      if (itemNum > levelNum && !merged.some(m => getId(m, 0) === itemId)) {
        merged.push({ ...mainItem });
      }
    });
    
    return merged;
  };

  /**
   * Merges batch project_data with main project_data based on restriction tag
   * Retains batch data up to the restricted point, appends main data after that
   * Also copies no_of_days from batch JSON for each task
   * 
   * Step-by-step:
   * 1. Validate inputs - return main data if batch data invalid
   * 2. Validate main data - return batch data if main data invalid
   * 3. Check if restriction tag exists - return main data if not
   * 4. Parse restriction tag to get phase/part/task/subtask numbers
   * 5. Extract content arrays from both data sources
   * 6. Define helper to find tasks in batch data structure
   * 7. Merge phases recursively using mergeItems helper
   * 8. Post-process merged phases to preserve no_of_days from batch for all tasks
   * 9. Return merged structure with batch data up to restriction, main data after
   */
  const mergeProjectData = (batchProjectData: any, mainProjectData: any): any => {
    // Step 1: Validate inputs - return main data if batch data invalid
    if (!batchProjectData || !batchProjectData.content) {
      return mainProjectData;
    }
    
    // Step 2: Validate main data - return batch data if main data invalid
    if (!mainProjectData || !mainProjectData.content) {
      return batchProjectData;
    }

    // Step 3: Check if restriction tag exists - return main data if not
    if (!projectLastUpdatableTag) {
      return mainProjectData;
    }

    // Step 4: Parse restriction tag to get phase/part/task/subtask numbers
    const tagData = parseUpdatableTag(projectLastUpdatableTag);
    if (!tagData) {
      return mainProjectData;
    }

    // Step 5: Extract content arrays from both data sources
    const batchContent = batchProjectData.content || [];
    const mainContent = mainProjectData.content || [];

    /**
     * Step 6: Helper function to find a task in batch data by phase_id, part_id, and task_id
     * Searches entire batch structure to find matching task
     */
    const findBatchTask = (phaseId: string, partId: string, taskId: string): any => {
      const batchPhase = batchContent.find((p: any) => 
        (p.phase_id || '').toLowerCase() === (phaseId || '').toLowerCase()
      );
      if (!batchPhase) return null;
      
      const batchPart = (batchPhase.parts || []).find((p: any) => 
        (p.part_id || '').toLowerCase() === (partId || '').toLowerCase()
      );
      if (!batchPart) return null;
      
      const batchTask = (batchPart.tasks || []).find((t: any) => 
        (t.task_id || '').toLowerCase() === (taskId || '').toLowerCase()
      );
      return batchTask || null;
    };

    /**
     * Step 7: Merge phases recursively using mergeItems helper
     * This will recursively merge parts, tasks, and subtasks based on restriction logic
     */
    const mergedPhases = mergeItems(
      batchContent,
      mainContent,
      tagData,
      'phase',
      tagData.phase,
      (item, index) => item.phase_id || `phase${index + 1}`,
      extractNumberFromId,
      (batchPhase, mainPhase) => {
        // Merge parts within same phase
        const batchParts = batchPhase.parts || [];
        const mainParts = mainPhase.parts || [];
        
        const mergedParts = mergeItems(
          batchParts,
          mainParts,
          tagData,
          'part',
          tagData.part,
          (item, index) => item.part_id || `part${index + 1}`,
          extractNumberFromId,
          (batchPart, mainPart) => {
            // Merge tasks within same part
            const batchTasks = batchPart.tasks || [];
            const mainTasks = mainPart.tasks || [];
            
            const mergedTasks = mergeItems(
              batchTasks,
              mainTasks,
              tagData,
              'task',
              tagData.task,
              (item, index) => item.task_id || `task${index + 1}`,
              extractNumberFromId,
              (batchTask, mainTask) => {
                // Merge subtasks within same task
                const batchSubtasks = batchTask.data || [];
                const mainSubtasks = mainTask.data || [];
                
                const mergedSubtasks = mergeItems(
                  batchSubtasks,
                  mainSubtasks,
                  tagData,
                  'subtask',
                  tagData.subtask,
                  (item, index) => item.subtask_id || `subtask${index + 1}`,
                  extractNumberFromId,
                  undefined,
                  true // Use inclusive comparison for subtasks (<=)
                );
                
                // Copy no_of_days from batch task
                return {
                  ...batchTask,
                  data: mergedSubtasks,
                  no_of_days: batchTask.no_of_days
                };
              }
            );
            
            // After merging, preserve no_of_days from batch for tasks that come from main data
            // This ensures tasks after the restriction also retain their no_of_days from batch
            // Search entire batch structure, not just current part (in case whole phase/part came from main)
            const tasksWithBatchDays = mergedTasks.map((mergedTask: any) => {
              // First try to find in current batchPart tasks
              let batchTask = batchTasks.find((bt: any) => 
                (bt.task_id || '').toLowerCase() === (mergedTask.task_id || '').toLowerCase()
              );
              
              // If not found, search entire batch structure using helper function
              if (!batchTask) {
                batchTask = findBatchTask(
                  mainPhase.phase_id || batchPhase.phase_id,
                  mainPart.part_id || batchPart.part_id,
                  mergedTask.task_id
                );
              }
              
              // If batch task exists and has no_of_days, preserve it
              if (batchTask && batchTask.no_of_days !== undefined && batchTask.no_of_days !== null) {
                return {
                  ...mergedTask,
                  no_of_days: batchTask.no_of_days
                };
              }
              
              return mergedTask;
            });
            
            return {
              ...batchPart,
              tasks: tasksWithBatchDays
            };
          }
        );
        
        return {
          ...batchPhase,
          parts: mergedParts
        };
      }
    );
    
    /**
     * Step 8: Post-process merged phases to preserve no_of_days from batch for all tasks
     * This handles cases where entire phases/parts come from main data (after restriction)
     * Iterates through all merged tasks and preserves no_of_days from batch if it exists
     */
    const processedPhases = mergedPhases.map((mergedPhase: any) => {
      const processedParts = (mergedPhase.parts || []).map((mergedPart: any) => {
        const processedTasks = (mergedPart.tasks || []).map((mergedTask: any) => {
          // Find corresponding batch task using helper function
          const batchTask = findBatchTask(
            mergedPhase.phase_id,
            mergedPart.part_id,
            mergedTask.task_id
          );
          
          // If batch task exists and has no_of_days, preserve it
          if (batchTask && batchTask.no_of_days !== undefined && batchTask.no_of_days !== null) {
            return {
              ...mergedTask,
              no_of_days: batchTask.no_of_days
            };
          }
          
          return mergedTask;
        });
        
        return {
          ...mergedPart,
          tasks: processedTasks
        };
      });
      
      return {
        ...mergedPhase,
        parts: processedParts
      };
    });
    
    /**
     * Step 9: Return merged structure with batch data up to restriction, main data after
     * Preserves all no_of_days values from batch data throughout the structure
     */
    return {
      ...mainProjectData,
      content: processedPhases
    };
  };

  /**
   * Step 3: Memoize merged project data - reuse this for both taskDays initialization and modal display
   * This expensive merge operation is only recalculated when dependencies change
   * Dependencies:
   * - commonProjectData: Common project_data from selected batches
   * - apiResponse?.project_data: Main project template data
   * - projectLastUpdatableTag: Restriction tag used in merge logic
   */
  const mergedProjectDataMemo = useMemo(() => {
    // Step 1: Check if main project_data exists
    if (!apiResponse?.project_data) return null;
    
    // Step 2: If selected batches have common project_data, merge it with main project_data
    if (commonProjectData && commonProjectData.content) {
      return mergeProjectData(commonProjectData, apiResponse.project_data);
    }
    
    // Step 3: Otherwise use main project_data (for new batches or no selection)
    return apiResponse.project_data;
  }, [commonProjectData, apiResponse?.project_data, projectLastUpdatableTag]);

  /**
   * Step 4: Initialize taskDays from merged project data when batches with same existing data are selected
   * This ensures taskDays matches the structure that will be displayed in the modal
   * Dependencies:
   * - mergedProjectDataMemo: Merged project data structure
   * - commonProjectData: Common project_data from selected batches (fallback)
   */
  useEffect(() => {
    // Step 1: If merged project data exists, extract taskDays from it
    if (mergedProjectDataMemo && mergedProjectDataMemo.content) {
      const initialTaskDays = extractTaskDaysFromData(mergedProjectDataMemo);
      setTaskDays(initialTaskDays);
    } 
    // Step 2: Fallback - initialize from commonProjectData if merge fails
    else if (commonProjectData && commonProjectData.content) {
      const initialTaskDays = extractTaskDaysFromData(commonProjectData);
      setTaskDays(initialTaskDays);
    } 
    // Step 3: Different data or no data - clear taskDays so they're calculated
    else {
      setTaskDays({});
    }
  }, [mergedProjectDataMemo, commonProjectData]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await authService.get('admin/batch/get_all_courses_for_batch/');
      setCourses(response.data.courses || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error fetching courses:', error);
      setCourses([]);
    }
  };

  const fetchCoursesWithChangedProjects = async () => {
    if (!projectId) return;
    try {
      const response = await authService.get(`admin/courses_with_changed_projects/${projectId}/`);
      const coursesWithCounts = response.data.courses || [];
      
      // Create a map of course_id to batches_count
      const batchesCountMap: Record<string, number> = {};
      coursesWithCounts.forEach((course: { course_id: string; batches_count: number }) => {
        batchesCountMap[course.course_id] = course.batches_count;
      });
      
      // Update courses with batches_count
      setCourses(prevCourses => 
        prevCourses.map(course => ({
          ...course,
          batches_count: batchesCountMap[course.course_id] || 0
        }))
      );
    } catch (error) {
      console.error('Error fetching courses with changed projects:', error);
    }
  };

  const fetchRules = async () => {
    try {
      const response = await authService.get('admin/fetch_rules/');
      setRules(response.data);
    } catch (error) {
      console.error('Error fetching rules:', error);
    }
  };

  const fetchBatchProjects = async (courseId: string, projectId: string) => {
    try {
      setLoadingBatches(true);
      const response = await authService.post('admin/batch_project_list/', {
        course_id: courseId,
        project_id: parseInt(projectId)
      });
      const data: BatchProjectResponse = response.data;
      setApiResponse(data);
      setBatchProjects(data.batch_project || []);
      setProjectLastUpdatableTag(data.project_last_updatable_tag || "");
      
      // Initialize local state with fetched data
      const initialData: Record<string, { 
        slots: number; 
        saturday_holiday: boolean; 
        sunday_holiday: boolean;
        start_date: string;
        no_of_hours: string;
      }> = {};
      (data.batch_project || []).forEach((batch) => {
        initialData[batch.batch_id] = {
          slots: batch.slots,
          saturday_holiday: batch.saturday_holiday,
          sunday_holiday: batch.sunday_holiday,
          start_date: batch.start_date,
          no_of_hours: batch.no_of_hours
        };
      });
      setLocalBatchData(initialData);
      
      // Don't initialize taskDays from batch data - it should be calculated for new batches
      // Only initialize if a batch with existing data is selected (handled in useEffect)
      setTaskDays({});
      
      setLoadingBatches(false);
    } catch (error) {
      setLoadingBatches(false);
      console.error('Error fetching batch projects:', error);
      setBatchProjects([]);
    }
  };

  const handleCourseChange = (event: SelectChangeEvent<string>) => {
    setSelectedCourse(event.target.value as string);
  };

  /**
   * Handles select all checkbox toggle
   */
  const handleSelectAll = () => {
    // Step 1: If currently selected all, deselect all batches
    if (selectAll) {
      setSelectedBatches(new Set());
      setSelectAll(false);
    } else {
      // Step 2: Check if batches have different project_data
      const batchesWithData = batchProjects.filter(b => b.project_data !== null && b.project_data !== undefined);
      
      if (batchesWithData.length > 0) {
        // Step 2: Check if all batches with data have the same project_data
        const firstData = batchesWithData[0].project_data;
        const allSame = batchesWithData.every(b => areProjectDataEqual(b.project_data, firstData));
        
        // Step 3: If validation fails, show error and return
        if (!allSame) {
          setErrorMessage("Cannot select all batches when batches have different project data.");
          setShowErrorModal(true);
          return;
        }
      }
      
      // Step 4: If validation passes, select all batches
      const allBatchIds = new Set(batchProjects.map(b => b.batch_id));
      setSelectedBatches(allBatchIds);
      setSelectAll(true);
    }
  };

  /**
   * Handles individual batch selection/deselection with validation
   * Memoized with useCallback to prevent unnecessary re-renders
   * Dependencies:
   * - batchProjects: All available batches
   * - selectedBatches: Currently selected batch IDs
   * - commonProjectData: Common project_data from selected batches
   */
  const handleBatchSelect = useCallback((batchId: string) => {
    // Step 1: Find the batch and check if it has existing project_data
    const batch = batchProjects.find(b => b.batch_id === batchId);
    const hasExistingData = batch?.project_data !== null && batch?.project_data !== undefined;
    
    const newSelected = new Set(selectedBatches);
    const isCurrentlySelected = newSelected.has(batchId);
    
    // Step 2: If deselecting, remove batch and update selectAll state
    if (isCurrentlySelected) {
      newSelected.delete(batchId);
      setSelectedBatches(newSelected);
      setSelectAll(newSelected.size === batchProjects.length);
      return;
    }
    
    // Step 3: If selecting and no batches selected yet, allow selection
    if (selectedBatches.size === 0) {
      newSelected.add(batchId);
      setSelectedBatches(newSelected);
      setSelectAll(newSelected.size === batchProjects.length);
      return;
    }
    
    // Step 4: Check existing selections for project_data
    const selectedBatchesList = Array.from(selectedBatches)
      .map(id => batchProjects.find(b => b.batch_id === id))
      .filter(b => b !== undefined) as BatchProject[];
    
    const batchesWithData = selectedBatchesList.filter(
      b => b.project_data !== null && b.project_data !== undefined
    );
    
    // Step 5: If no existing selections have data, allow selecting this batch
    if (batchesWithData.length === 0) {
      newSelected.add(batchId);
      setSelectedBatches(newSelected);
      setSelectAll(newSelected.size === batchProjects.length);
      return;
    }
    
    // Step 6: If existing selections have data, validate this batch matches
    if (hasExistingData) {
      // Check if this batch's data matches the common data of currently selected batches
      const currentCommonData = commonProjectData;
      if (currentCommonData && areProjectDataEqual(batch.project_data, currentCommonData)) {
        // Same data - allow selection
        newSelected.add(batchId);
        setSelectedBatches(newSelected);
        setSelectAll(newSelected.size === batchProjects.length);
        return;
      } else {
        // Step 7: Different data - prevent selection and show error
        setErrorMessage("Cannot select batches with different project data. Please deselect batches with different data first.");
        setShowErrorModal(true);
        return;
      }
    } else {
      // Step 7: This batch has no data, but existing selections have data - prevent selection
      setErrorMessage("Cannot select batches without project data when batches with project data are already selected.");
      setShowErrorModal(true);
      return;
    }
  }, [batchProjects, selectedBatches, commonProjectData]);

  const handleSlotChange = useCallback((batchId: string, value: string) => {
    const numValue = parseInt(value) || 0;
    setLocalBatchData((prev) => ({
      ...prev,
      [batchId]: {
        ...prev[batchId],
        slots: numValue
      }
    }));
  }, []);

  const convertDateToInputFormat = (dateString: string): string => {
    if (!dateString) return "";
    try {
      // Parse "Jan 1 2026" format
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      // Convert to YYYY-MM-DD for date input
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      return "";
    }
  };

  const getProjectDataValidationIssues = (): ProjectConfigIssue[] => {
    const issues: ProjectConfigIssue[] = [];

    if (!apiResponse || !apiResponse.project_data) {
      issues.push({
        phase: "—",
        detail: "Project data is missing from the server response.",
      });
      return issues;
    }

    const projectData = apiResponse.project_data;

    if (!projectData.content || !Array.isArray(projectData.content) || projectData.content.length === 0) {
      issues.push({
        phase: "—",
        detail: "No phases configured: content is missing, not an array, or empty.",
      });
      return issues;
    }

    for (let phaseIndex = 0; phaseIndex < projectData.content.length; phaseIndex++) {
      const phase = projectData.content[phaseIndex];
      const phaseLabel =
        phase?.phase_name && typeof phase.phase_name === "string" && phase.phase_name.trim() !== ""
          ? phase.phase_name.trim()
          : `Phase ${phaseIndex + 1} (unnamed)`;

      if (!phase.phase_name || typeof phase.phase_name !== "string" || phase.phase_name.trim() === "") {
        issues.push({
          phase: phaseLabel,
          detail: "Phase name is missing or empty.",
        });
        continue;
      }

      if (!phase.parts || !Array.isArray(phase.parts)) {
        issues.push({
          phase: phaseLabel,
          detail: "Parts list is missing or invalid for this phase.",
        });
        continue;
      }

      for (let partIndex = 0; partIndex < phase.parts.length; partIndex++) {
        const part = phase.parts[partIndex];
        const partLabel =
          part?.part_name && typeof part.part_name === "string" && part.part_name.trim() !== ""
            ? part.part_name.trim()
            : `Part ${partIndex + 1} (unnamed)`;

        if (!part.part_name || typeof part.part_name !== "string" || part.part_name.trim() === "") {
          issues.push({
            phase: phaseLabel,
            part: partLabel,
            detail: "Part name is missing or empty.",
          });
          continue;
        }

        if (!part.tasks || !Array.isArray(part.tasks)) {
          issues.push({
            phase: phaseLabel,
            part: partLabel,
            detail: "Tasks list is missing or invalid for this part.",
          });
          continue;
        }

        for (let taskIndex = 0; taskIndex < part.tasks.length; taskIndex++) {
          const task = part.tasks[taskIndex];
          const taskLabel =
            task?.task_name && typeof task.task_name === "string" && task.task_name.trim() !== ""
              ? task.task_name.trim()
              : `Task ${taskIndex + 1} (unnamed)`;

          if (!task.task_name || typeof task.task_name !== "string" || task.task_name.trim() === "") {
            issues.push({
              phase: phaseLabel,
              part: partLabel,
              task: taskLabel,
              detail: "Task name is missing or empty.",
            });
            continue;
          }

          if (!task.data || !Array.isArray(task.data)) {
            issues.push({
              phase: phaseLabel,
              part: partLabel,
              task: taskLabel,
              detail: "Subtasks (data array) are missing or invalid for this task.",
            });
            continue;
          }

          for (let subtaskIndex = 0; subtaskIndex < task.data.length; subtaskIndex++) {
            const subtask = task.data[subtaskIndex];
            const subLabel =
              subtask?.subtask_name && typeof subtask.subtask_name === "string" && subtask.subtask_name.trim() !== ""
                ? subtask.subtask_name.trim()
                : `Subtask ${subtaskIndex + 1} (unnamed)`;

            if (!subtask.subtask_name || typeof subtask.subtask_name !== "string" || subtask.subtask_name.trim() === "") {
              issues.push({
                phase: phaseLabel,
                part: partLabel,
                task: taskLabel,
                subtask: subLabel,
                detail: "Subtask name is missing or empty.",
              });
              continue;
            }

            if ((subtask.type === "video" || subtask.type === "notes") && !subtask.id) {
              issues.push({
                phase: phaseLabel,
                part: partLabel,
                task: taskLabel,
                subtask: subLabel,
                detail: `${subtask.type === "video" ? "Video" : "Notes"} subtask is missing content id.`,
              });
            }

            if (subtask.type === "coding" || subtask.type === "mcq") {
              const level1 = subtask.level1 || [];
              const level2 = subtask.level2 || [];
              const level3 = subtask.level3 || [];
              const hasLevel1 = Array.isArray(level1) && level1.length > 0;
              const hasLevel2 = Array.isArray(level2) && level2.length > 0;
              const hasLevel3 = Array.isArray(level3) && level3.length > 0;

              if (!hasLevel1 && !hasLevel2 && !hasLevel3) {
                issues.push({
                  phase: phaseLabel,
                  part: partLabel,
                  task: taskLabel,
                  subtask: subLabel,
                  detail: `${subtask.type.toUpperCase()} subtask has no questions in level1, level2, or level3.`,
                });
              }
            }
          }
        }
      }
    }

    return issues;
  };

  const validateSelectedBatches = (): { valid: boolean; message: string } => {
    if (selectedBatches.size === 0) {
      return { valid: false, message: "Please select at least one batch" };
    }

    const batchesWithZeroSlots: string[] = [];
    selectedBatches.forEach((batchId) => {
      const batch = batchProjects.find(b => b.batch_id === batchId);
      const localData = localBatchData[batchId];
      const slots = localData?.slots ?? batch?.slots ?? 0;
      
      if (slots === 0) {
        const batchName = batch?.batch_name || batchId;
        batchesWithZeroSlots.push(batchName);
      }
    });

    if (batchesWithZeroSlots.length > 0) {
      return { 
        valid: false, 
        message: `Cannot map batches with 0 slots. Please set slots for: ${batchesWithZeroSlots.join(", ")}` 
      };
    }

    return { valid: true, message: "" };
  };

  const handleMapClick = () => {
    const validation = validateSelectedBatches();
    if (!validation.valid) {
      setErrorMessage(validation.message);
      setShowErrorModal(true);
      return;
    }

    // Validate project data
    const projectIssues = getProjectDataValidationIssues();
    if (projectIssues.length > 0) {
      setConfigModalIssues(projectIssues);
      setShowConfigModal(true);
      return;
    }

    // Show slot times modal first
    setShowSlotTimesModal(true);
  };

  const handleSlotTimesSave = (
    startTime: string,
    endTime: string,
    startDateInput: string,
    noOfHoursValue: string,
    saturdayHolidayValue: boolean,
    sundayHolidayValue: boolean
  ) => {
    setSlotStartTime(startTime);
    setSlotEndTime(endTime);

    // Convert YYYY-MM-DD input to "Mon DD YYYY" format
    // Parse as UTC to avoid timezone shifts
    let formattedDate = startDateInput;
    if (startDateInput) {
      const date = dayjs.utc(startDateInput, "YYYY-MM-DD", true);
      if (date.isValid()) {
        formattedDate = date.format("MMM D YYYY");
      }
    }

    // Apply common settings to all selected batches
    setLocalBatchData((prev) => {
      const updated = { ...prev };
      const batchIdsToUpdate = Array.from(selectedBatches);

      batchIdsToUpdate.forEach((id) => {
        if (!updated[id]) {
          return;
        }
        updated[id] = {
          ...updated[id],
          start_date: formattedDate,
          no_of_hours: noOfHoursValue,
          saturday_holiday: saturdayHolidayValue,
          sunday_holiday: sundayHolidayValue
        };
      });

      return updated;
    });

    setShowSlotTimesModal(false);
    // Now show the project data modal
    setShowMapModal(true);
  };

  const convertDateToISO = (dateString: string): string => {
    if (!dateString) return "";
    try {
      let date: dayjs.Dayjs | null = null;
      
      // Try parsing "MMM D YYYY" format (e.g., "Dec 30 2025")
      // Parse manually to avoid timezone issues
      const mmmMatch = dateString.match(/^(\w{3})\s+(\d{1,2})\s+(\d{4})$/);
      if (mmmMatch) {
        const [, monthStr, dayStr, yearStr] = mmmMatch;
        const month = MONTH_NAMES_TO_NUMBER[monthStr];
        if (month !== undefined) {
          const day = parseInt(dayStr, 10);
          const year = parseInt(yearStr, 10);
          // Create UTC date using Date.UTC and convert to dayjs
          const utcTimestamp = Date.UTC(year, month, day, 0, 0, 0, 0);
          date = dayjs.utc(utcTimestamp);
        }
      }
      
      // If that fails, try YYYY-MM-DD format
      if (!date || !date.isValid()) {
        date = dayjs.utc(dateString, "YYYY-MM-DD", true);
      }
      
      // If still invalid, try parsing as ISO string
      if (!date || !date.isValid()) {
        date = dayjs.utc(dateString);
      }
      
      if (date && date.isValid()) {
        // Set time to 00:00:00 UTC explicitly to avoid timezone shifts
        date = date.hour(0).minute(0).second(0).millisecond(0);
        
        // Format as UTC ISO string: YYYY-MM-DDTHH:mm:ssZ
        const isoString = date.format("YYYY-MM-DD[T]HH:mm:ss[Z]");
        return isoString;
      }
      
      return "";
    } catch (error) {
      console.error("Error in convertDateToISO:", error);
      return "";
    }
  };

  /**
   * Adds no_of_days to each task in project_data
   * @param projectData - Project data structure to add days to
   * @param useBatchDays - If true, use taskDays state for existing batches; if false, always calculate
   */
  const addNoOfDaysToProjectData = (projectData: any, useBatchDays: boolean = false): any => {
    // Step 1: Validate project data structure
    if (!projectData || !projectData.content) return projectData;

    // Step 2-5: Iterate through phases, parts, and tasks to add no_of_days
    const updatedContent = projectData.content.map((phase: any, phaseIndex: number) => ({
      ...phase,
      parts: phase.parts.map((part: any, partIndex: number) => ({
        ...part,
        tasks: part.tasks.map((task: any, taskIndex: number) => {
          const taskKey = getTaskKey(phaseIndex, partIndex, taskIndex);
          
          // Step 3: Calculate task days from subtask times and hours per day
          const calculatedDays = calculateDays(
            task.data.reduce((sum: number, subtask: any) => sum + getTimeForSubtask(subtask, rules), 0),
            noOfHours
          );
          
          // Step 4-5: Determine which days value to use
          let noOfDays: number;
            noOfDays = useBatchDays && taskDays[taskKey] !== undefined 
              ? taskDays[taskKey] 
              : calculatedDays;
            noOfDays = taskDays[taskKey] !== undefined ? taskDays[taskKey] : calculatedDays;
          const roundedDays = Math.round(noOfDays * 100) / 100;
          
          return {
            ...task,
            no_of_days: roundedDays
          };
        })
      }))
    }));

    // Step 6: Return updated project data with no_of_days added
    return {
      ...projectData,
      content: updatedContent
    };
  };

  /**
   * Handles saving batch project mappings to the server
   */
  const handleSave = async () => {
    // Step 1: Validate that apiResponse and selectedBatchData exist
    if (!apiResponse || !selectedBatchData) return;

    // Step 2: Validate that all selected batches have slots > 0
    const batchesWithZeroSlots: string[] = [];
    selectedBatches.forEach((batchId) => {
      const batch = batchProjects.find(b => b.batch_id === batchId);
      const localData = localBatchData[batchId];
      const slots = localData?.slots ?? batch?.slots ?? 0;
      
      if (slots === 0) {
        const batchName = batch?.batch_name || batchId;
        batchesWithZeroSlots.push(batchName);
      }
    });

    // Step 3: Show error if validation fails
    if (batchesWithZeroSlots.length > 0) {
      setErrorMessage(`Cannot map batches with 0 slots. Please set slots for: ${batchesWithZeroSlots.join(", ")}`);
      setShowErrorModal(true);
      return;
    }

    try {
      setSaving(true);
      
      // Step 4: Reuse pre-calculated merged project data if available (optimization)
      // mergedProjectDataMemo already contains the merged data if commonProjectData exists
      const mergedProjectDataForBatches = mergedProjectDataMemo;
      
      // Step 5: Map each selected batch to save payload
      const batches = Array.from(selectedBatches).map((batchId) => {
        const batch = batchProjects.find(b => b.batch_id === batchId);
        const localData = localBatchData[batchId];
        
        if (!batch || !localData) return null;

        // Step 5a: Convert start_date to ISO format
        const startDateISO = convertDateToISO(localData.start_date);
        
        /**
         * Step 5b: Parse start date as UTC to calculate end date (avoid timezone issues)
         * Handles multiple date formats: "MMM D YYYY", "YYYY-MM-DD", and ISO strings
         */
        const parseDateAsUTC = (dateString: string): dayjs.Dayjs => {
          let date: dayjs.Dayjs | null = null;
          
          // Try parsing "MMM D YYYY" format manually to avoid timezone issues
          const mmmMatch = dateString.match(/^(\w{3})\s+(\d{1,2})\s+(\d{4})$/);
          if (mmmMatch) {
            const [, monthStr, dayStr, yearStr] = mmmMatch;
            const month = MONTH_NAMES_TO_NUMBER[monthStr];
            if (month !== undefined) {
              const day = parseInt(dayStr, 10);
              const year = parseInt(yearStr, 10);
              const utcTimestamp = Date.UTC(year, month, day, 0, 0, 0, 0);
              date = dayjs.utc(utcTimestamp);
            }
          }
          
          // If that fails, try YYYY-MM-DD format
          if (!date || !date.isValid()) {
            date = dayjs.utc(dateString, "YYYY-MM-DD", true);
          }
          
          // If still invalid, try parsing as ISO string
          if (!date || !date.isValid()) {
            date = dayjs.utc(dateString);
          }
          
          return date;
        };
        
        // Step 5b: Parse start date as UTC
        const startDate = parseDateAsUTC(localData.start_date);
        
        /**
         * Step 5c: Calculate end_date based on start_date and total days (working in UTC)
         * The end date should be start_date + (totalDays - 1) days
         * Example: Start Dec 30, 4 days → Dec 30 (day 1), Dec 31 (day 2), Jan 1 (day 3), Jan 2 (day 4, end date)
         * 
         * Algorithm:
         * - Start counting from day 1 (start date)
         * - Add days one by one, skipping holidays
         * - Stop when we've counted totalDays
         */
        let endDate = startDate;
        let daysCounted = 1; // Start date is day 1
        const totalDays = totals.totalDays;
        
        // We need to count totalDays, starting from day 1 (start date)
        // So we need to add (totalDays - 1) more days
        while (daysCounted < totalDays) {
          // Move to next day first
          endDate = endDate.add(1, 'day');
          
          // Check if this day should be skipped (holiday)
          const nextDayOfWeek = endDate.day();
          const nextIsSaturday = nextDayOfWeek === 6;
          const nextIsSunday = nextDayOfWeek === 0;
          
          if ((nextIsSaturday && localData.saturday_holiday) || (nextIsSunday && localData.sunday_holiday)) {
            continue; // Skip this day, don't count it, loop will continue
          }
          
          // This day counts
          daysCounted++;
        }
        
        // Format end date as UTC ISO with 00:00:00 for time: YYYY-MM-DDTHH:mm:ssZ
        const endDateISO = endDate.hour(0).minute(0).second(0).millisecond(0).format("YYYY-MM-DD[T]HH:mm:ss[Z]");

        /**
         * Step 5d: Determine project_data to use (merged or individual)
         * - If batch has existing data and batches have same data: use pre-merged common data
         * - If batch has existing data but different: merge its own data
         * - If batch has no data: use main project_data template
         */
        const hasExistingData = batch.project_data !== null && batch.project_data !== undefined;
        let projectDataToUse = apiResponse?.project_data;
        
        if (hasExistingData && mergedProjectDataForBatches) {
          // Batch has existing data and selected batches have same data - use pre-merged common data
          projectDataToUse = mergedProjectDataForBatches;
        } else if (hasExistingData) {
          // Batch has existing data but different from other selected batches - merge its own data
          projectDataToUse = mergeProjectData(batch.project_data, apiResponse?.project_data);
        }
        // For new batches, projectDataToUse is already set to main project_data
        
        /**
         * Step 5e: Add no_of_days to project_data
         * - For new batches (hasExistingData = false): always calculate days
         * - For existing batches (hasExistingData = true): use taskDays if available, otherwise calculate
         */
        const updatedProjectData = addNoOfDaysToProjectData(projectDataToUse, hasExistingData);

        return {
          batch_id: batch.batch_id,
          batch_name: batch.batch_name,
          slots: localData.slots,
          saturday_holiday: localData.saturday_holiday,
          sunday_holiday: localData.sunday_holiday,
          no_of_hours: parseFloat(localData.no_of_hours) || 0,
          start_date: startDateISO,
          end_date: endDateISO,
          slot_start_time: slotStartTime,
          slot_end_time: slotEndTime,
          project_score: totals.totalScore,
          duration_in_days: totalDays,
          project_data: updatedProjectData
        };
      }).filter(b => b !== null);

      // Step 6: Create payload with all batch data
      const payload = {
        project_id: parseInt(projectId),
        course_id: selectedCourse,
        batches: batches,
        by: sessionStorage.getItem("Email") || ""
      };

      // Step 7: Send to API
      await authService.post('admin/create_batch_project/', payload);
      
      setSaving(false);
      setShowMapModal(false);
      
      // Step 8: Clear selected batches after successful save
      setSelectedBatches(new Set());
      setSelectAll(false);
      
      // Step 8: Refresh the data to show updated batch information
      if (selectedCourse && projectId) {
        fetchBatchProjects(selectedCourse, projectId);
      }
    } catch (error) {
      // Step 9: Handle errors
      setSaving(false);
      console.error('Error saving batch project:', error);
      setErrorMessage("Error saving batch project. Please try again.");
      setShowErrorModal(true);
    }
  };

  const formatTimeRange = (startTime: string, endTime: string) => {
    if (!startTime || !endTime) return "-";
    return `${startTime} - ${endTime}`;
  };

  const calculateDays = useCallback((timeInMinutes: number, noOfHours: string): number => {
    const hoursPerDay = parseFloat(noOfHours) || 2;
    const minutesPerDay = hoursPerDay * 60;
    const calculatedDays = timeInMinutes / minutesPerDay;
    const rounded = Math.round(calculatedDays * 100) / 100; // Round to 2 decimal places (0.12, 0.25, etc.)
    return Math.max(0.01, rounded); // Minimum 0.01 day
  }, []);

  const getTaskKey = (phaseIndex: number, partIndex: number, taskIndex: number): string => {
    return `${phaseIndex}-${partIndex}-${taskIndex}`;
  };

  const getTaskDays = useCallback((phaseIndex: number, partIndex: number, taskIndex: number, calculatedDays: number): number => {
    const key = getTaskKey(phaseIndex, partIndex, taskIndex);
    const daysValue = taskDays[key] !== undefined ? taskDays[key] : calculatedDays;
    // Ensure value is rounded to 2 decimal places when retrieving/displaying
    return Math.round(daysValue * 100) / 100;
  }, [taskDays]);

  const handleTaskDaysChange = useCallback((phaseIndex: number, partIndex: number, taskIndex: number, value: string) => {
    const numValue = parseFloat(value) || 0.01;
    const key = getTaskKey(phaseIndex, partIndex, taskIndex);
    // Round to 2 decimal places to ensure precision
    const roundedValue = Math.round(numValue * 100) / 100;
    setTaskDays((prev) => ({
      ...prev,
      [key]: Math.max(0.01, roundedValue) // Minimum 0.01 day, always 2 decimal places
    }));
  }, []);

  const calculateEstimatedEndDate = (startDate: string, totalDays: number, saturdayHoliday: boolean, sundayHoliday: boolean): string => {
    if (!startDate) return "";
    try {
      // Try parsing "MMM D YYYY" format first
      let start = dayjs(startDate, "MMM D YYYY", true);
      
      // If that fails, try YYYY-MM-DD format
      if (!start.isValid()) {
        start = dayjs(startDate, "YYYY-MM-DD", true);
      }
      
      // If still invalid, try parsing as ISO string
      if (!start.isValid()) {
        start = dayjs(startDate);
      }
      
      if (!start.isValid()) return "";
      
      let currentDate = start;
      let daysAdded = 0;
      
      while (daysAdded < totalDays) {
        const dayOfWeek = currentDate.day();
        const isSaturday = dayOfWeek === 6;
        const isSunday = dayOfWeek === 0;
        
        // Skip if it's a holiday
        if ((isSaturday && saturdayHoliday) || (isSunday && sundayHoliday)) {
          currentDate = currentDate.add(1, 'day');
          continue;
        }
        
        daysAdded++;
        if (daysAdded < totalDays) {
          currentDate = currentDate.add(1, 'day');
        }
      }
      
      return currentDate.format("MMM D YYYY");
    } catch (error) {
      return "";
    }
  };

  const getTimeForSubtask = useCallback((subtask: any, rules: RulesResponse | null): number => {
    if (subtask.type === "video" || subtask.type === "notes" || subtask.type === "project_coding") {
      return parseInt(subtask.time || "0");
    } else if (subtask.type === "mcq" && rules) {
      let totalTime = 0;
      // New structure: level1, level2, level3 are arrays of IDs
      const level1 = Array.isArray(subtask.level1) ? subtask.level1.length : 0;
      const level2 = Array.isArray(subtask.level2) ? subtask.level2.length : 0;
      const level3 = Array.isArray(subtask.level3) ? subtask.level3.length : 0;
      
      const rule1 = rules.mcq.find(r => r.level.toLowerCase() === "level1");
      const rule2 = rules.mcq.find(r => r.level.toLowerCase() === "level2");
      const rule3 = rules.mcq.find(r => r.level.toLowerCase() === "level3");
      
      if (rule1) totalTime += level1 * parseInt(rule1.time || "0");
      if (rule2) totalTime += level2 * parseInt(rule2.time || "0");
      if (rule3) totalTime += level3 * parseInt(rule3.time || "0");
      
      return totalTime;
    } else if (subtask.type === "coding" && rules) {
      let totalTime = 0;
      // New structure: level1, level2, level3 are arrays of IDs
      const level1 = Array.isArray(subtask.level1) ? subtask.level1.length : 0;
      const level2 = Array.isArray(subtask.level2) ? subtask.level2.length : 0;
      const level3 = Array.isArray(subtask.level3) ? subtask.level3.length : 0;
      
      const rule1 = rules.coding.find(r => r.level.toLowerCase() === "level1");
      const rule2 = rules.coding.find(r => r.level.toLowerCase() === "level2");
      const rule3 = rules.coding.find(r => r.level.toLowerCase() === "level3");
      
      if (rule1) totalTime += level1 * parseInt(rule1.time || "0");
      if (rule2) totalTime += level2 * parseInt(rule2.time || "0");
      if (rule3) totalTime += level3 * parseInt(rule3.time || "0");
      
      return totalTime;
    }
    return 0;
  }, []);

  const getScoreForSubtask = useCallback((subtask: any, rules: RulesResponse | null): number => {
    if (subtask.type === "project_coding" && rules?.project_coding) {
      const levelKey = (subtask.selectedContentLevel || subtask.level || "level1").toString().toLowerCase();
      const rule = rules.project_coding.find((r) => r.level.toLowerCase() === levelKey);
      return rule ? parseInt(rule.score || "0") : 0;
    } else if (subtask.type === "mcq" && rules) {
      let totalScore = 0;
      // New structure: level1, level2, level3 are arrays of IDs
      const level1 = Array.isArray(subtask.level1) ? subtask.level1.length : 0;
      const level2 = Array.isArray(subtask.level2) ? subtask.level2.length : 0;
      const level3 = Array.isArray(subtask.level3) ? subtask.level3.length : 0;
      
      const rule1 = rules.mcq.find(r => r.level.toLowerCase() === "level1");
      const rule2 = rules.mcq.find(r => r.level.toLowerCase() === "level2");
      const rule3 = rules.mcq.find(r => r.level.toLowerCase() === "level3");
      
      if (rule1) totalScore += level1 * parseInt(rule1.score || "0");
      if (rule2) totalScore += level2 * parseInt(rule2.score || "0");
      if (rule3) totalScore += level3 * parseInt(rule3.score || "0");
      
      return totalScore;
    } else if (subtask.type === "coding" && rules) {
      let totalScore = 0;
      // New structure: level1, level2, level3 are arrays of IDs
      const level1 = Array.isArray(subtask.level1) ? subtask.level1.length : 0;
      const level2 = Array.isArray(subtask.level2) ? subtask.level2.length : 0;
      const level3 = Array.isArray(subtask.level3) ? subtask.level3.length : 0;
      
      const rule1 = rules.coding.find(r => r.level.toLowerCase() === "level1");
      const rule2 = rules.coding.find(r => r.level.toLowerCase() === "level2");
      const rule3 = rules.coding.find(r => r.level.toLowerCase() === "level3");
      
      if (rule1) totalScore += level1 * parseInt(rule1.score || "0");
      if (rule2) totalScore += level2 * parseInt(rule2.score || "0");
      if (rule3) totalScore += level3 * parseInt(rule3.score || "0");
      
      return totalScore;
    }
    return 0;
  }, []);

  /**
   * Calculates totals for project data (time, score, tasks, days)
   */
  const calculateTotals = (projectData: ProjectData | null, noOfHours: string) => {
    // Step 1: Validate project data structure and rules
    if (!projectData || !projectData.content || !Array.isArray(projectData.content) || projectData.content.length === 0 || !rules) {
      return { totalTime: 0, totalScore: 0, totalTasks: 0, totalDays: 0 };
    }

    // Step 2: Initialize counters
    let totalTime = 0;
    let totalScore = 0;
    let totalTasks = 0;
    let totalDays = 0;

    // Step 3: Iterate through phases, parts, and tasks
    projectData.content.forEach((phase, phaseIndex) => {
      phase.parts.forEach((part, partIndex) => {
        part.tasks.forEach((task, taskIndex) => {
          totalTasks++;
          
          // Step 4a-b: Count total time and score from all subtasks
          let taskTotalTime = 0;
          task.data.forEach((subtask) => {
            taskTotalTime += getTimeForSubtask(subtask, rules);
            totalScore += getScoreForSubtask(subtask, rules);
          });
          totalTime += taskTotalTime;
          
          // Step 4c-d: Calculate task days and use custom if available
          const calculatedTaskDays = calculateDays(taskTotalTime, noOfHours);
          const taskKey = getTaskKey(phaseIndex, partIndex, taskIndex);
          const taskDaysValue = taskDays[taskKey] !== undefined ? taskDays[taskKey] : calculatedTaskDays;
          totalDays += taskDaysValue;
        });
      });
    });

    // Step 5: Return totals
    return { totalTime, totalScore, totalTasks, totalDays };
  };

  const togglePhase = useCallback((phaseIndex: number) => {
    setExpandedPhases(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(phaseIndex)) {
        newExpanded.delete(phaseIndex);
      } else {
        newExpanded.add(phaseIndex);
      }
      return newExpanded;
    });
  }, []);

  const togglePart = useCallback((phaseIndex: number, partIndex: number) => {
    setExpandedParts(prev => {
      const key = `${phaseIndex}`;
      const newExpanded = { ...prev };
      if (!newExpanded[key]) {
        newExpanded[key] = new Set();
      }
      const partSet = new Set(newExpanded[key]);
      if (partSet.has(partIndex)) {
        partSet.delete(partIndex);
      } else {
        partSet.add(partIndex);
      }
      newExpanded[key] = partSet;
      return newExpanded;
    });
  }, []);

  // Reuse mergedProjectDataMemo for modal - no need to recalculate
  const getMergedProjectDataForModal = mergedProjectDataMemo;

  // Memoize selected batch data to avoid recalculating on every render
  /**
   * Memoize selected batch data to avoid recalculating on every render
   * Dependencies: selectedBatchesArray (stable array), localBatchData (object)
   */
  const selectedBatchData = useMemo(() => {
    return selectedBatchesArray.length > 0 
      ? localBatchData[selectedBatchesArray[0]]
      : null;
  }, [selectedBatchesArray, localBatchData]);

  const noOfHours = selectedBatchData?.no_of_hours || "";
  
  /**
   * Memoize selected batch for modal initialization
   * Dependencies: selectedBatchesArray (stable array), batchProjects (array)
   */
  const selectedBatchForModal = useMemo(() => {
    return selectedBatchesArray.length > 0
      ? batchProjects.find(b => b.batch_id === selectedBatchesArray[0])
      : null;
  }, [selectedBatchesArray, batchProjects]);

  const mergedProjectData = getMergedProjectDataForModal; // Alias for readability
  
  /**
   * Memoize totals calculation - expensive operation that iterates through all tasks
   * Dependencies:
   * - mergedProjectData: Merged project data structure
   * - apiResponse?.project_data: Fallback main project data
   * - noOfHours: Hours per day for calculation
   * - rules: Rules for time/score calculation
   * - taskDays: Custom task days from user input
   */
  const totals = useMemo(() => {
    return calculateTotals(mergedProjectData || apiResponse?.project_data, noOfHours);
  }, [mergedProjectData, apiResponse?.project_data, noOfHours, rules, taskDays]);

  const totalDays = totals.totalDays; // Total duration is sum of all task days
  
  /**
   * Memoize estimated end date calculation
   * Dependencies:
   * - selectedBatchData: Selected batch data with start date and holidays
   * - totalDays: Total duration calculated from tasks
   */
  const estimatedEndDate = useMemo(() => {
    return selectedBatchData 
      ? calculateEstimatedEndDate(
          selectedBatchData.start_date, 
          totalDays, 
          selectedBatchData.saturday_holiday, 
          selectedBatchData.sunday_holiday
        )
      : "";
  }, [selectedBatchData, totalDays]);

  return (
    <div className="border rounded-2 bg-white my-2 me-2" style={{ height: `calc(100vh - 90px)`, overflowY: "auto" }}>
      <div className="d-flex justify-content-between mt-2 pe-3">
        <div className="d-flex justify-content-start align-items-center">
          <span className="d-flex justify-content-start align-items-center ps-4">
            <MUIFormControl
              variant="outlined"
              size="small"
              style={{ minWidth: 140 }}
            >
              <InputLabel id="course-select-label">
                Course
              </InputLabel>
              <MUISelect
                labelId="course-select-label"
                id="course-select"
                value={selectedCourse}
                onChange={handleCourseChange}
                label="Course"
                disabled={loading || !projectId}
                style={{
                  height: "36px"
                }}
              >
                <MenuItem value="">
                  Select Course
                </MenuItem>
                {courses.map((course) => (
                  <MenuItem key={course.course_id} value={course.course_id}>
                    {course.course_name}
                    {course.batches_count !== undefined && course.batches_count > 0 && (
                      <span className="ms-2 text-danger">({course.batches_count})</span>
                    )}
                  </MenuItem>
                ))}
              </MUISelect>
            </MUIFormControl>
          </span>
        </div>
        {selectedBatches.size > 0 && (
          <Button variant="primary" onClick={handleMapClick}>
            Assign Batch
          </Button>
        )}
      </div>

      <div className="pt-4">
        <div className="m-0 p-0 px-4" style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 170px)" }}>
          {loadingBatches ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : batchProjects.length > 0 ? (
            <TableContainer 
              component={Paper} 
              sx={{ 
                flex: 1,
                overflowY: "auto",
                overflowX: "auto",
                borderRadius: 0
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {["", "S.No", "Batch", "Slot", "No of Hours", "Sat Off", "Sun Off", "Start Date", "End Date", "Selection TimeLine"].map((heading) => (
                      <TableCell
                        key={heading}
                        sx={{
                          fontWeight: "normal",
                          backgroundColor: "#fff",
                          position: "sticky",
                          top: 0,
                          zIndex: 2
                        }}
                      >
                        {heading === "" ? (
                          <Checkbox
                            checked={selectAll}
                            onChange={handleSelectAll}
                            size="small"
                          />
                        ) : (
                          heading
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batchProjects.map((batch, index) => {
                    const localData = localBatchData[batch.batch_id] || {
                      slots: batch.slots,
                      saturday_holiday: batch.saturday_holiday,
                      sunday_holiday: batch.sunday_holiday,
                      start_date: batch.start_date,
                      no_of_hours: batch.no_of_hours
                    };
                    const isSelected = selectedBatches.has(batch.batch_id);
                    
                    return (
                      <TableRow 
                        key={batch.batch_id}
                        sx={{
                          backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                          "&:hover": {
                            backgroundColor: "#f0f0f0"
                          }
                        }}
                      >
                        <TableCell>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleBatchSelect(batch.batch_id)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          {batch.batch_name}
                          {batch.project_changed_since_last_tag && (
                            <span className="text-danger">{" "}*</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            inputProps={{ min: 0 }}
                            value={localData.slots}
                            onChange={(e) => handleSlotChange(batch.batch_id, e.target.value)}
                            disabled={!isSelected}
                            size="small"
                            sx={{ width: "80px" }}
                          />
                        </TableCell>
                        <TableCell>{localData.no_of_hours}</TableCell>
                        <TableCell align="center">{localData.saturday_holiday ? "Yes" : "No"}</TableCell>
                        <TableCell align="center">{localData.sunday_holiday ? "Yes" : "No"}</TableCell>
                        <TableCell>{localData.start_date || "-"}</TableCell>
                        <TableCell>{batch.end_date || "-"}</TableCell>
                        <TableCell>{formatTimeRange(batch.slot_start_time, batch.slot_end_time)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : selectedCourse ? (
            <div className="text-center py-4">
              <p className="text-muted">No batches found for this course and project</p>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted">Please select a course to view batches</p>
            </div>
          )}
        </div>
      </div>

      {/* Error Modal */}
      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Validation Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>{errorMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Configuration Modal */}
      <Modal show={showConfigModal} onHide={() => setShowConfigModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Project Configuration Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            The project data is incomplete or not configured. Fix the issues below in project configuration, then try mapping batches again.
          </p>
          {configModalIssues.length > 0 ? (
            <ul className="list-unstyled mb-0" style={{ maxHeight: "50vh", overflowY: "auto" }}>
              {configModalIssues.map((issue, idx) => (
                <li
                  key={idx}
                  className="border rounded p-2 mb-2 bg-light"
                  style={{ fontSize: "0.9rem" }}
                >
                  <div className="mb-1">
                    <strong>Phase:</strong> {issue.phase}
                    {issue.part != null && issue.part !== "" && (
                      <>
                        {" "}
                        <strong>· Part:</strong> {issue.part}
                      </>
                    )}
                    {issue.task != null && issue.task !== "" && (
                      <>
                        {" "}
                        <strong>· Task:</strong> {issue.task}
                      </>
                    )}
                    {issue.subtask != null && issue.subtask !== "" && (
                      <>
                        {" "}
                        <strong>· Subtask:</strong> {issue.subtask}
                      </>
                    )}
                  </div>
                  <div className="text-danger mb-0">
                    <strong>Issue:</strong> {issue.detail}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted mb-0">No specific issues were recorded.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfigModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Slot Times Modal */}
      <SlotTimesModal
        show={showSlotTimesModal}
        onHide={() => setShowSlotTimesModal(false)}
        onSave={handleSlotTimesSave}
        initialSlotStartTime={selectedBatchForModal?.slot_start_time || undefined}
        initialSlotEndTime={selectedBatchForModal?.slot_end_time || undefined}
        initialStartDate={selectedBatchForModal?.start_date ? convertDateToInputFormat(selectedBatchForModal.start_date) : (selectedBatchData?.start_date ? convertDateToInputFormat(selectedBatchData.start_date) : "")}
        initialNoOfHours={selectedBatchForModal?.no_of_hours || selectedBatchData?.no_of_hours || ""}
        initialSaturdayHoliday={selectedBatchForModal?.saturday_holiday ?? selectedBatchData?.saturday_holiday ?? false}
        initialSundayHoliday={selectedBatchForModal?.sunday_holiday ?? selectedBatchData?.sunday_holiday ?? false}
      />

      {/* Project Data Modal */}
      <ProjectDataModal
        show={showMapModal}
        onHide={() => setShowMapModal(false)}
        onSave={handleSave}
        projectData={getMergedProjectDataForModal}
        rules={rules}
        noOfHours={noOfHours}
        totals={{ totalScore: totals.totalScore, totalDays: totalDays }}
        estimatedEndDate={estimatedEndDate}
        expandedPhases={expandedPhases}
        expandedParts={expandedParts}
        taskDays={taskDays}
        onTogglePhase={togglePhase}
        onTogglePart={togglePart}
        onTaskDaysChange={handleTaskDaysChange}
        getTimeForSubtask={getTimeForSubtask}
        getScoreForSubtask={getScoreForSubtask}
        calculateDays={calculateDays}
        getTaskDays={getTaskDays}
        saving={saving}
      />
    </div>
  );
};

export default ProjectBatchMap;
